import {
  BufferAttribute,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  Quaternion,
  SRGBColorSpace,
  SphereGeometry,
  Vector3,
  VideoTexture,
  Color,
} from "three";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";

import { Context } from "../../context";

import { stadiumGltfLoader } from "./stadium";
import { defaultScale } from "../../constants";

export class Jumbotron extends Object3D {
  private board?: Object3D;
  private video?: HTMLVideoElement;
  private material?: MeshBasicMaterial;

  private contextRef: Context;

  helpers: Object3D;
  screen?: Object3D;

  white: Color = new Color(0xffffff);
  black: Color = new Color(0x000000);

  private screenDims?: {
    width: number;
    height: number;
  };

  constructor(context: Context) {
    super();

    this.helpers = new Object3D();
    this.contextRef = context;
  }

  async init(assetBase: string, homeAbbr: string) {
    this.board = (await this.fetch(assetBase, homeAbbr))?.scene;
    if (!this.board) return;

    this.board.scale.setScalar(defaultScale);

    // NB: we use the actual mesh for NYY and WSH, otherwise we make plane
    //     at correct AR
    const useMesh = ["WSH", "NYY"].includes(homeAbbr);
    this.screen = this.findScreen(this.board, useMesh);

    if (this.screen) this.add(this.screen);
    this.add(this.board);
  }

  teardown() {
    if (this.video && !this.video.paused) {
      this.video.pause();
    }
    this.video = undefined;
    this.material?.map?.dispose();
  }

  playVideo(url: string, cb: () => void) {
    if (this.video !== undefined) {
      console.warn(
        "Triggered playing another video on the jumbotron while one was already playing."
      );
    }

    this.video = document.createElement("video");
    this.video.setAttribute("crossorigin", "anonymous");
    this.video.setAttribute("playsinline", "");
    this.video.setAttribute("style", "display: none");
    this.video.setAttribute("height", "720");
    this.video.setAttribute("width", "1280");
    this.video.setAttribute("src", url);

    // When the video ends, remove the src and unset the reference so it gets garbage collected
    this.video.addEventListener("ended", () => {
      if (this.material && cb && typeof cb === "function") {
        cb();

        this.video?.removeAttribute("src");
        this.video = undefined;
        this.material.map?.dispose();
        this.material.color = this.black;
        this.material.needsUpdate = true;
      }
    });

    if (this.material) {
      // dispose of the old texture if it still exists
      this.material.map?.dispose();
      this.material.color = this.white;

      const texture = new VideoTexture(this.video);
      texture.colorSpace = SRGBColorSpace;
      texture.flipY = true;

      this.material.map = texture;
      this.material.needsUpdate = true;
    }
  }

  update() {
    if (!this.video) return;

    const playing = this.contextRef.state.playing;
    const rate = this.contextRef.state.playbackSpeed;

    if (this.video.paused && playing) {
      this.video.play();
    } else if (!this.video.paused && !playing) {
      this.video.pause();
    }

    this.video.playbackRate = rate;
  }

  arcAngle(camPos: Vector3): number {
    if (!this.screen || !this.screenDims) return 0;

    const dist = camPos.distanceTo(this.screen.position);
    return (Math.atan2(this.screenDims.width, dist) * 180) / Math.PI;
  }

  private async fetch(
    assetBase: string,
    homeAbbr: string
  ): Promise<GLTF | undefined> {
    const jumboPath = `${assetBase}/models/ballparks/screens/${homeAbbr}.glb`;

    const headResp = await fetch(jumboPath, { method: "HEAD" });
    if (headResp.status !== 200) {
      console.error("can't fetch separate jumbotron geometry");
      return undefined;
    }

    return await stadiumGltfLoader.loadAsync(jumboPath);
  }

  private findScreen(board: Object3D, useMesh = false): Object3D | undefined {
    // DEBUG remove
    const ball = new Mesh(
      new SphereGeometry(7.5, 32, 8),
      new MeshBasicMaterial({ color: 0xff0000 })
    );
    this.helpers.add(ball);

    const mesh = board.getObjectByName("Screen");
    if (!mesh || !(mesh instanceof Mesh)) {
      console.error("failure to find screen geometry");
      return undefined;
    }

    const buffer: BufferAttribute = mesh.geometry.attributes[
      "position"
    ] as BufferAttribute;

    if (buffer.count < 4) {
      console.warn("corrupt screen geometry", buffer.count);
      return undefined;
    }

    const screen = new Object3D();
    screen.add(this.helpers);

    const min = new Vector3(Infinity, Infinity, 0);
    const max = new Vector3(-Infinity, -Infinity, 0);

    const points: Vector3[] = [];
    // find centroid
    for (let i = 0; i < buffer.count; i++) {
      const x = buffer.getX(i);
      const y = buffer.getY(i);
      const z = buffer.getZ(i);

      screen.position.x += x;
      screen.position.y += y;
      screen.position.z += z;

      if (x < min.x) {
        min.x = x;
        min.z = z;
      }
      if (y < min.y) {
        min.y = y;
      }

      if (x > max.x) {
        max.x = x;
        max.z = z;
      }
      if (y > max.y) {
        max.y = y;
      }

      const point = new Vector3(x, y, z);
      point.multiplyScalar(defaultScale);
      points.push(point);
    }

    screen.position.multiplyScalar(defaultScale / buffer.count);

    min.multiplyScalar(defaultScale);
    max.multiplyScalar(defaultScale);

    const extents = {
      top: {
        left: new Vector3(min.x, max.y, min.z),
        right: new Vector3(max.x, max.y, max.z),
      },
      bottom: {
        left: new Vector3(min.x, min.y, min.z),
        right: new Vector3(max.x, min.y, max.z),
      },
    };

    const tl = new Mesh(
      new SphereGeometry(5, 32, 8),
      new MeshBasicMaterial({ color: 0xff3456 })
    );
    const tr = new Mesh(
      new SphereGeometry(5, 32, 8),
      new MeshBasicMaterial({ color: 0xff3456 })
    );
    const bl = new Mesh(
      new SphereGeometry(5, 32, 8),
      new MeshBasicMaterial({ color: 0xff3456 })
    );
    const br = new Mesh(
      new SphereGeometry(5, 32, 8),
      new MeshBasicMaterial({ color: 0xff3456 })
    );
    tl.position.copy(extents.top.left);
    tr.position.copy(extents.top.right);
    bl.position.copy(extents.bottom.left);
    br.position.copy(extents.bottom.right);
    this.helpers.attach(tl);
    this.helpers.attach(tr);
    this.helpers.attach(bl);
    this.helpers.attach(br);

    const helperOffset = 20;
    // find normal
    const normalBall = new Mesh(
      new SphereGeometry(5, 32, 8),
      new MeshBasicMaterial({ color: 0x00ff00 })
    );
    this.helpers.add(normalBall);

    const normal = new Vector3();
    normal
      .crossVectors(
        extents.top.right.sub(extents.top.left),
        extents.top.left.sub(extents.bottom.left)
      )
      .normalize();

    normalBall.position.add(normal.multiplyScalar(helperOffset));

    // find ortho
    const orthoBall = new Mesh(
      new SphereGeometry(5, 32, 8),
      new MeshBasicMaterial({ color: 0x0000ff })
    );
    this.helpers.add(orthoBall);

    const up = new Vector3(0, 1, 0);
    const ortho = new Vector3();
    ortho.crossVectors(normal, up).normalize();

    orthoBall.position.add(ortho.multiplyScalar(helperOffset));

    up.crossVectors(ortho, normal).normalize();
    const upBall = new Mesh(
      new SphereGeometry(5, 32, 8),
      new MeshBasicMaterial({ color: 0xffffff })
    );
    upBall.position.add(up.multiplyScalar(helperOffset));
    this.helpers.add(upBall);

    const material = new MeshBasicMaterial({ color: 0x000000 });

    const AR = 1280 / 720;
    let height = max.y - min.y;
    let width = Math.sqrt((max.x - min.x) ** 2 + (max.z - min.z) ** 2);
    if (width > height * AR) {
      width = height * AR;
    } else {
      height = width / AR;
    }
    this.screenDims = {
      width,
      height,
    };

    // make screen
    if (useMesh) {
      // just use mesh
      mesh.material = material;
      screen.attach(mesh);
    } else {
      const videoScreen = new Mesh(
        new PlaneGeometry(width, height, 8, 8),
        material
      );
      videoScreen.position.z = 0.1;

      // find rotation
      const q = new Quaternion().setFromUnitVectors(
        new Vector3(-1, 0, 0),
        ortho.normalize()
      );
      videoScreen.setRotationFromQuaternion(q);
      screen.add(videoScreen);
    }
    this.material = material;

    this.helpers.visible = false;

    return screen;
  }
}
